<template>
  <v-container fluid class="bus-dispatching-page pa-3">
    <!-- ══ Hero Header ══ -->
    <div class="dispatch-hero mb-2">
      <v-layout row align-center>
        <v-flex>
          <v-layout row align-center>
            <v-icon class="dispatch-hero-icon mr-2" size="22"
              >mdi-headset</v-icon
            >
            <div class="dispatch-hero-title">Transportation Dispatching</div>
            <span class="dispatch-scope-chip">
              <v-icon x-small class="mr-1">mdi-bus-alert</v-icon>Bus Changes
            </span>
            <div class="dispatch-hero-sub">
              Live bus change lookup for dispatcher calls
            </div>
          </v-layout>
        </v-flex>
        <v-spacer></v-spacer>
        <v-btn
          color="white"
          outline
          dark
          small
          class="dispatch-refresh-btn"
          :loading="loading"
          @click="loadDispatcherData"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-layout>
    </div>

    <!-- ══ Filter Panel ══ -->
    <v-card class="filter-card mb-3">
      <!-- Header bar -->
      <div class="filter-header">
        <v-icon class="filter-header-icon mr-2">mdi-tune</v-icon>
        <span class="filter-header-title">Dispatcher Lookup</span>
        <v-chip
          v-if="activeFilterCount > 0"
          small
          class="filter-active-chip ml-2"
          color="green lighten-5"
          text-color="green darken-3"
        >
          {{ activeFilterCount }} active
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn
          flat
          small
          class="filter-clear-btn"
          :disabled="activeFilterCount === 0"
          @click="clearFilters"
        >
          <v-icon left small>mdi-filter-remove</v-icon>
          Clear Filters
        </v-btn>
      </div>

      <div class="filter-body">
        <!-- Quick search — full width prominent -->
        <div class="filter-search-wrap mb-2">
          <v-text-field
            v-model="filters.generalSearch"
            prepend-inner-icon="mdi-magnify"
            label="Search anything — student, address, phone, note, bus…"
            clearable
            solo
            hide-details
            class="filter-main-search"
          ></v-text-field>
        </div>

        <!-- Narrow filters row -->
        <div class="filter-group-label">Narrow by</div>
        <v-layout row wrap class="filter-fields-row">
          <v-flex xs12 sm4 md2 class="filter-field-wrap">
            <v-text-field
              v-model="filters.studentName"
              prepend-inner-icon="mdi-account"
              label="Student Name"
              clearable
              outline
              dense
              hide-details
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 md2 class="filter-field-wrap">
            <v-autocomplete
              v-model="filters.busNumber"
              :items="availableBusNumbers"
              prepend-inner-icon="mdi-bus"
              label="Bus #"
              clearable
              outline
              dense
              hide-details
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm4 md2 class="filter-field-wrap">
            <v-text-field
              v-model="filters.phoneNumber"
              prepend-inner-icon="mdi-phone"
              label="Phone Number"
              mask="###-###-####"
              clearable
              outline
              dense
              hide-details
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 md2 class="filter-field-wrap">
            <v-autocomplete
              v-model="filters.branch"
              :items="availableBranches"
              prepend-inner-icon="mdi-source-branch"
              label="Branch"
              clearable
              outline
              dense
              hide-details
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm4 md2 class="filter-field-wrap">
            <v-select
              v-model="filters.status"
              :items="availableStatuses"
              prepend-inner-icon="mdi-flag"
              label="Status"
              clearable
              outline
              dense
              hide-details
            ></v-select>
          </v-flex>
          <v-flex xs12 sm4 md2 class="filter-field-wrap">
            <v-menu
              v-model="dateRangeMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateRangeDisplay"
                  prepend-inner-icon="mdi-calendar-range"
                  label="Date Range"
                  placeholder="All dates"
                  clearable
                  outline
                  dense
                  hide-details
                  readonly
                  v-on="on"
                  @click:clear="clearDateRange"
                ></v-text-field>
              </template>
              <v-card class="date-range-card">
                <div class="date-range-presets">
                  <div
                    v-for="preset in datePresets"
                    :key="preset.value"
                    class="date-range-preset"
                    @click="applyDatePreset(preset.value)"
                  >
                    {{ preset.label }}
                  </div>
                </div>

                <div class="dr-cal" @mouseleave="rangeHoverDate = ''">
                  <div
                    v-for="(month, monthIndex) in calendarMonths"
                    :key="month.key"
                    class="dr-cal-month"
                  >
                    <div class="dr-cal-head">
                      <v-btn
                        icon
                        small
                        class="dr-cal-nav"
                        :class="{ 'dr-cal-nav--hidden': monthIndex !== 0 }"
                        @click="shiftCalendar(-1)"
                      >
                        <v-icon>mdi-chevron-left</v-icon>
                      </v-btn>
                      <div class="dr-cal-title">{{ month.label }}</div>
                      <v-btn
                        icon
                        small
                        class="dr-cal-nav"
                        :class="{
                          'dr-cal-nav--hidden':
                            monthIndex !== calendarMonths.length - 1,
                        }"
                        @click="shiftCalendar(1)"
                      >
                        <v-icon>mdi-chevron-right</v-icon>
                      </v-btn>
                    </div>
                    <div class="dr-cal-dow">
                      <span v-for="(dow, dowIndex) in dayNames" :key="dowIndex">{{ dow }}</span>
                    </div>
                    <div class="dr-cal-grid">
                      <div
                        v-for="(cell, cellIndex) in month.cells"
                        :key="month.key + '-' + cellIndex"
                        class="dr-cell"
                        :class="cellClasses(cell)"
                        @click="cell.date && selectCalendarDate(cell.date)"
                        @mouseenter="cell.date && (rangeHoverDate = cell.date)"
                      >
                        <span v-if="cell.date" class="dr-day">{{
                          cell.day
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="date-range-actions">
                  <span class="date-range-hint">{{ dateRangeHint }}</span>
                  <v-spacer></v-spacer>
                  <v-btn flat small @click="clearDateRange">
                    <v-icon left small>mdi-close</v-icon>
                    Clear
                  </v-btn>
                  <v-btn small color="green darken-2" dark @click="dateRangeMenu = false">
                    Done
                  </v-btn>
                </div>
              </v-card>
            </v-menu>
          </v-flex>
        </v-layout>

        <!-- Toggle row -->
        <div class="filter-toggle-row mt-2">
          <div
            class="filter-toggle-pill"
            :class="{ 'filter-toggle-pill--on': isActiveToday }"
            @click="toggleActiveToday"
          >
            <v-icon small class="mr-1">mdi-calendar-today</v-icon>
            Active Today Only
          </div>
          <div
            class="filter-toggle-pill"
            :class="{ 'filter-toggle-pill--yesterday': isActiveYesterday }"
            @click="toggleActiveYesterday"
          >
            <v-icon small class="mr-1">mdi-calendar-minus</v-icon>
            Active Yesterday
          </div>
          <div
            class="filter-toggle-pill"
            :class="{ 'filter-toggle-pill--completed': filters.includeCompleted }"
            @click="filters.includeCompleted = !filters.includeCompleted"
          >
            <v-icon small class="mr-1">mdi-check-circle</v-icon>
            Include Completed
          </div>
          <div
            class="filter-toggle-pill"
            :class="{ 'filter-toggle-pill--deleted': filters.includeDeleted }"
            @click="filters.includeDeleted = !filters.includeDeleted"
          >
            <v-icon small class="mr-1">mdi-delete</v-icon>
            Include Deleted
          </div>
        </div>
      </div>
    </v-card>

    <!-- ══ Results summary bar ══ -->
    <div class="results-bar mb-2">
      <div class="results-bar-inner">
        <div class="results-count-block">
          <span class="results-count-num">{{ filteredBusChanges.length }}</span>
          <span class="results-count-label">
            of {{ busChanges.length }} bus changes
          </span>
        </div>

        <div class="results-divider"></div>

        <div class="results-status-row">
          <div
            v-for="entry in statusCounts"
            :key="entry.status"
            class="results-status-chip"
            :class="{
              'results-status-chip--on': filters.status === entry.status,
            }"
            :style="
              filters.status === entry.status
                ? { borderColor: entry.hex, background: entry.hex }
                : { borderColor: entry.hex, color: entry.hex }
            "
            :title="
              filters.status === entry.status
                ? 'Clear status filter'
                : 'Show only ' + entry.status
            "
            @click="toggleStatusFilter(entry.status)"
          >
            {{ entry.status }}
            <strong class="ml-1">{{ entry.count }}</strong>
          </div>
          <span v-if="!statusCounts.length" class="results-empty-note">
            No matches
          </span>
        </div>

        <v-spacer></v-spacer>

        <div class="results-meta">
          <span class="results-meta-item">
            <v-icon small class="mr-1">mdi-calendar-range</v-icon>
            {{ dateRangeDisplay || "All dates" }}
          </span>
          <span v-if="uniqueBusCount" class="results-meta-item">
            <v-icon small class="mr-1">mdi-bus</v-icon>
            {{ uniqueBusCount }} buses
          </span>
          <span v-if="userBranch" class="results-meta-item">
            <v-icon small class="mr-1">mdi-source-branch</v-icon>
            {{ userBranch }}
          </span>
        </div>
      </div>
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        height="3"
        class="mt-1"
      ></v-progress-linear>
    </div>

    <!-- ══ Empty state ══ -->
    <v-alert
      v-if="
        !loading && busChanges.length > 0 && filteredBusChanges.length === 0
      "
      type="info"
      outline
      class="mt-2"
    >
      No bus changes matched the current lookup filters.
    </v-alert>

    <v-alert
      v-if="!loading && busChanges.length === 0"
      type="info"
      outline
      class="mt-2"
    >
      No bus changes loaded. Click Refresh to reload.
    </v-alert>

    <!-- ══ Results: expansion panels (Vuetify 1.x API) ══ -->
    <v-expansion-panel
      class="dispatch-panels"
      v-if="filteredBusChanges.length > 0"
    >
      <v-expansion-panel-content
        v-for="busChange in filteredBusChanges"
        :key="busChange.id"
      >
        <!-- Header slot -->
        <template v-slot:header>
          <div class="panel-header-inner">
            <!-- status bar on left -->
            <div
              class="panel-status-bar"
              :style="{ backgroundColor: getStatusHex(busChange.status) }"
            ></div>

            <!-- student / account on the left -->
            <div class="panel-col panel-name-col">
              <div class="panel-name-line">
                <div class="panel-student-heb" v-if="busChange.studentNameHeb">
                  {{ busChange.studentNameHeb }}
                </div>
                <div class="panel-student-name">
                  {{ busChange.studentName || "Unknown Student" }}
                </div>
              </div>
              <div v-if="busChange.homeAddress" class="panel-home-row" :title="busChange.homeAddress">
                <span class="panel-home-label">Home Address:</span>
                <span class="panel-home-val">{{ busChange.homeAddress }}</span>
              </div>
              <div v-if="busChange.homePhone" class="panel-home-row">
                <span class="panel-home-label">Home #:</span>
                <span class="panel-home-val">{{ formatPhoneNumber(busChange.homePhone) }}</span>
              </div>
            </div>

            <!-- bus badge(s) -->
            <div class="panel-col panel-bus-col">
              <div class="panel-field-label">BUS #</div>
              <div
                v-if="busChange.hasDifferentBusNumbers"
                class="panel-bus-dual-row"
              >
                <div
                  class="panel-bus-badge panel-bus-badge--sm"
                  :style="{ borderColor: getStatusHex(busChange.status) }"
                >
                  <span class="panel-bus-dir">AM</span
                  >{{ busChange.pickupBusNumber }}
                </div>
                <div
                  class="panel-bus-badge panel-bus-badge--sm"
                  :style="{ borderColor: '#ff9800' }"
                >
                  <span class="panel-bus-dir">PM</span
                  >{{ busChange.dropoffBusNumber }}
                </div>
              </div>
              <div
                v-else
                class="panel-bus-badge"
                :style="{ borderColor: getStatusHex(busChange.status) }"
              >
                {{ busChange.primaryBusNumber || "—" }}
              </div>
            </div>

            <!-- combined date + period, single line -->
            <div class="panel-col panel-dates-col">
              <div class="panel-field-label">SCHEDULE</div>
              <div class="panel-schedule-value">
                <span class="schedule-part">{{
                  formatShortSchedule(busChange.startDate, busChange.startPeriod)
                }}</span>
                <template v-if="showScheduleEnd(busChange)">
                  <v-icon small class="schedule-arrow">mdi-arrow-right</v-icon>
                  <span class="schedule-part">{{
                    formatShortSchedule(
                      busChange.endDate || busChange.startDate,
                      busChange.endPeriod
                    )
                  }}</span>
                </template>
              </div>
            </div>

            <!-- bus change data on the right (class removed when collapsed) -->
            <div class="panel-col panel-contact-col">
              <div class="panel-field-label">BUS CHANGE</div>
              <div v-if="busChange.busChangeName" class="panel-contact-name">
                {{ busChange.busChangeName }}
              </div>
              <div
                v-if="busChange.busChangeAddress"
                class="panel-contact-address"
                :title="busChange.busChangeAddress"
              >
                {{ busChange.busChangeAddress }}
              </div>
              <div v-if="busChange.busChangePhoneNumber" class="panel-contact-phone">
                <v-icon x-small class="mr-1" color="#2e7d32">mdi-phone</v-icon>{{ formatPhoneNumber(busChange.busChangePhoneNumber) }}
              </div>
            </div>

            <!-- status chip -->
            <div class="panel-col panel-status-col">
              <v-chip
                small
                :color="getStatusColor(busChange.status)"
                text-color="white"
                class="panel-status-chip"
              >
                <v-icon left small color="white">{{
                  getStatusIcon(busChange.status)
                }}</v-icon>
                {{ busChange.status || "UNKNOWN" }}
              </v-chip>
            </div>
          </div>
        </template>

        <!-- Expanded body -->
        <div class="panel-body">
          <v-layout row wrap>
            <!-- Left: bus change info -->
            <v-flex xs12 sm6 class="pr-2">
              <div class="detail-section">
                <div class="detail-section-title">
                  <v-icon small class="mr-1">mdi-bus-clock</v-icon>
                  Bus Change Details
                </div>
                <div class="detail-field">
                  <span class="detail-label">Schedule</span>
                  <span class="detail-val panel-schedule-value">
                    <span class="schedule-part">{{
                      formatShortSchedule(busChange.startDate, busChange.startPeriod)
                    }}</span>
                    <template v-if="showScheduleEnd(busChange)">
                      <v-icon small class="schedule-arrow">mdi-arrow-right</v-icon>
                      <span class="schedule-part">{{
                        formatShortSchedule(
                          busChange.endDate || busChange.startDate,
                          busChange.endPeriod
                        )
                      }}</span>
                    </template>
                  </span>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Bus Change Name</span>
                  <span class="detail-val">{{
                    busChange.busChangeName || "—"
                  }}</span>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Address</span>
                  <span class="detail-val">{{
                    busChange.busChangeAddress || "—"
                  }}</span>
                </div>
                <div class="detail-field mt-1" v-if="busChange.note">
                  <span class="detail-label">Note</span>
                  <span class="detail-val note-val">{{ busChange.note }}</span>
                </div>
                <div class="phone-row phone-row--primary mt-2">
                  <div class="phone-item phone-item--primary">
                    <v-icon small class="phone-icon">mdi-phone</v-icon>
                    <div>
                      <div class="detail-label">Bus Change Phone</div>
                      <div class="phone-number">
                        {{ formatPhoneNumber(busChange.busChangePhoneNumber) }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="minor-info-row mt-2">
                  <div class="minor-info-item">
                    <v-icon x-small class="minor-info-icon"
                      >mdi-message-text</v-icon
                    >
                    <span class="minor-info-label">Notification:</span>
                    <span class="minor-info-val">
                      {{ busChange.notificationType || "—" }}
                      <template v-if="busChange.notificationPhoneNumber">
                        ({{
                          formatPhoneNumber(busChange.notificationPhoneNumber)
                        }})
                      </template>
                    </span>
                  </div>
                  <div class="minor-info-item" v-if="busChange.geoPhoneNumber">
                    <v-icon x-small class="minor-info-icon"
                      >mdi-map-marker-radius</v-icon
                    >
                    <span class="minor-info-label">Geo Phone:</span>
                    <span class="minor-info-val">{{
                      formatPhoneNumber(busChange.geoPhoneNumber)
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- Bus info: display-only cards, mirrors the route/stop picker layout -->
              <div
                class="detail-section bus-info-section"
                v-if="busChange.pickupRouteID || busChange.dropoffRouteID"
              >
                <div class="detail-section-title">
                  <v-icon small class="mr-1">mdi-bus</v-icon>
                  Bus
                </div>
                <div class="bus-cards-row">
                <div class="bus-card" v-if="busChange.pickupRouteID">
                  <div class="bus-card-top">
                    <div
                      class="bus-badge-lg"
                      :style="
                        getRouteChipStyle({ color: busChange.pickupRouteColor })
                      "
                    >
                      {{ busChange.pickupBusNumber || "—" }}
                    </div>
                    <div class="bus-card-details">
                      <div class="bus-card-route-label">Pickup Route</div>
                      <div class="bus-card-desc">
                        {{ busChange.pickupRouteDescription || "—" }}
                      </div>
                      <div
                        class="bus-card-driver"
                        v-if="busChange.pickupDriver"
                      >
                        <v-icon x-small>mdi-account</v-icon>
                        {{ busChange.pickupDriver }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="bus-card-stop"
                    :class="{ 'bus-card-stop--empty': !busChange.pickupStopNumber }"
                  >
                    <template v-if="busChange.pickupStopNumber">
                      <span class="stop-badge">{{
                        busChange.pickupStopNumber
                      }}</span>
                      <span class="stop-address">{{
                        busChange.pickupStopAddress ||
                          "Stop " + busChange.pickupStopNumber
                      }}</span>
                    </template>
                    <template v-else>
                      <v-icon x-small class="stop-empty-icon"
                        >mdi-map-marker-off</v-icon
                      >
                      <span>Stop not selected</span>
                    </template>
                  </div>
                </div>
                <div class="bus-card" v-if="busChange.dropoffRouteID">
                  <div class="bus-card-top">
                    <div
                      class="bus-badge-lg"
                      :style="
                        getRouteChipStyle({
                          color: busChange.dropoffRouteColor,
                        })
                      "
                    >
                      {{ busChange.dropoffBusNumber || "—" }}
                    </div>
                    <div class="bus-card-details">
                      <div class="bus-card-route-label">Dropoff Route</div>
                      <div class="bus-card-desc">
                        {{ busChange.dropoffRouteDescription || "—" }}
                      </div>
                      <div
                        class="bus-card-driver"
                        v-if="busChange.dropoffDriver"
                      >
                        <v-icon x-small>mdi-account</v-icon>
                        {{ busChange.dropoffDriver }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="bus-card-stop"
                    :class="{ 'bus-card-stop--empty': !busChange.dropoffStopNumber }"
                  >
                    <template v-if="busChange.dropoffStopNumber">
                      <span class="stop-badge">{{
                        busChange.dropoffStopNumber
                      }}</span>
                      <span class="stop-address">{{
                        busChange.dropoffStopAddress ||
                          "Stop " + busChange.dropoffStopNumber
                      }}</span>
                    </template>
                    <template v-else>
                      <v-icon x-small class="stop-empty-icon"
                        >mdi-map-marker-off</v-icon
                      >
                      <span>Stop not selected</span>
                    </template>
                  </div>
                </div>
                </div>
              </div>
            </v-flex>

            <!-- Right: student / account / class -->
            <v-flex xs12 sm6>
              <div class="detail-section">
                <div class="detail-section-title">
                  <v-icon small class="mr-1">mdi-account</v-icon>
                  Student, Account &amp; Class
                </div>
                <div class="detail-field" v-if="busChange.studentNameHeb">
                  <span class="detail-label">Hebrew Name</span>
                  <span class="detail-val teacher-val--rtl">{{
                    busChange.studentNameHeb
                  }}</span>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Student</span>
                  <span class="detail-val">{{
                    busChange.studentName || "—"
                  }}</span>
                </div>
                <div class="detail-field" v-if="busChange.homePhone">
                  <span class="detail-label">Home Phone</span>
                  <span class="detail-val home-phone-val">{{
                    formatPhoneNumber(busChange.homePhone)
                  }}</span>
                </div>
                <div class="detail-field" v-if="busChange.homeAddress">
                  <span class="detail-label">Home Address</span>
                  <span class="detail-val">{{ busChange.homeAddress }}</span>
                </div>
                <div class="lookup-ids-row">
                  <span class="lookup-id-item" v-if="busChange.familyID">
                    <span class="lookup-id-label">Family ID</span>
                    <span class="lookup-id-val">{{ busChange.familyID }}</span>
                  </span>
                  <span class="lookup-id-item" v-if="busChange.studentID">
                    <span class="lookup-id-label">Student ID</span>
                    <span class="lookup-id-val">{{ busChange.studentID }}</span>
                  </span>
                </div>
                <div
                  v-if="busChange.branch || busChange.class || busChange.building || busChange.section || busChange.teacherH || busChange.teacherH2 || getRoomDisplay(busChange)"
                  class="teacher-info-box mt-2"
                >
                  <div class="teacher-info-title">
                    <v-icon small class="mr-1" color="#2e7d32">mdi-school</v-icon>
                    {{
                      isBusChangeInSummerSeason(busChange)
                        ? "Class & Room"
                        : "Class & Teacher"
                    }}
                  </div>
                  <div class="teacher-fields-grid">
                    <div class="teacher-row" v-if="busChange.building">
                      <span class="teacher-label">Building</span>
                      <span class="teacher-val">{{ busChange.building }}</span>
                    </div>
                    <div class="teacher-row" v-if="busChange.branch">
                      <span class="teacher-label">Branch</span>
                      <span class="teacher-val">{{ busChange.branch }}</span>
                    </div>
                    <div class="teacher-row" v-if="busChange.class">
                      <span class="teacher-label">Class</span>
                      <span class="teacher-val">{{ busChange.class }}</span>
                    </div>
                    <div class="teacher-row" v-if="busChange.section">
                      <span class="teacher-label">Section</span>
                      <span class="teacher-val">{{ busChange.section }}</span>
                    </div>
                    <div
                      class="teacher-row"
                      v-if="
                        isBusChangeInSummerSeason(busChange) &&
                          getRoomDisplay(busChange)
                      "
                    >
                      <span class="teacher-label">DC Room</span>
                      <span class="teacher-val">{{
                        getRoomDisplay(busChange)
                      }}</span>
                    </div>
                    <div
                      class="teacher-row"
                      v-else-if="
                        !isBusChangeInSummerSeason(busChange) &&
                          (busChange.teacherH || busChange.teacherH2)
                      "
                    >
                      <span class="teacher-label">{{ busChange.teacherH2 ? "Teachers" : "Teacher" }}</span>
                      <span class="teacher-val teacher-val--rtl">
                        {{ busChange.teacherH }}
                        <template v-if="busChange.teacherH && busChange.teacherH2"> / </template>
                        {{ busChange.teacherH2 }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </v-flex>
          </v-layout>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-container>
</template>

<script>
import moment from "moment";

export default {
  data: () => ({
    loading: false,
    busRoutes: [],
    busRouteLookup: {},
    routeStopsLookup: {},
    busChanges: [],
    summerDates: null,
    userDayCampBuildings: [],
    dateRangeMenu: false,
    calendarMonth: moment()
      .startOf("month")
      .format("YYYY-MM-DD"),
    rangeHoverDate: "",
    dayNames: ["S", "M", "T", "W", "T", "F", "S"],
    datePresets: [
      { label: "Today", value: "today" },
      { label: "Yesterday", value: "yesterday" },
      { label: "This Week", value: "thisWeek" },
      { label: "Next 7 Days", value: "next7" },
      { label: "All Dates", value: "all" },
    ],
    filters: {
      generalSearch: "",
      busNumber: "",
      phoneNumber: "",
      studentName: "",
      branch: null,
      status: null,
      includeCompleted: false,
      includeDeleted: false,
      startDate: moment().format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
    },
  }),
  computed: {
    todayDate() {
      return moment().format("YYYY-MM-DD");
    },
    yesterdayDate() {
      return moment()
        .subtract(1, "day")
        .format("YYYY-MM-DD");
    },
    isActiveToday() {
      return (
        this.filters.startDate === this.todayDate &&
        this.filters.endDate === this.todayDate
      );
    },
    isActiveYesterday() {
      return (
        this.filters.startDate === this.yesterdayDate &&
        this.filters.endDate === this.yesterdayDate
      );
    },
    dateRangeDisplay() {
      const start = this.filters.startDate;
      const end = this.filters.endDate;
      if (!start && !end) return "";
      if (start && end) {
        if (start === end) return this.formatShortDate(start);
        return `${this.formatShortDate(start)} – ${this.formatShortDate(end)}`;
      }
      if (start) return `From ${this.formatShortDate(start)}`;
      return `Until ${this.formatShortDate(end)}`;
    },
    calendarMonths() {
      return [0, 1].map((offset) => {
        const monthStart = moment(this.calendarMonth).add(offset, "month");
        const daysInMonth = monthStart.daysInMonth();
        const leadingBlanks = monthStart.day();
        const cells = [];
        for (let i = 0; i < leadingBlanks; i++) {
          cells.push({ date: "", day: "" });
        }
        for (let day = 1; day <= daysInMonth; day++) {
          const date = monthStart.clone().date(day);
          cells.push({ date: date.format("YYYY-MM-DD"), day: day });
        }
        return {
          key: monthStart.format("YYYY-MM"),
          label: monthStart.format("MMMM YYYY"),
          cells: cells,
        };
      });
    },
    // While picking an end date, hovering previews the range.
    pendingRangeEnd() {
      if (!this.filters.startDate || this.filters.endDate) return "";
      if (!this.rangeHoverDate) return "";
      return this.rangeHoverDate > this.filters.startDate
        ? this.rangeHoverDate
        : "";
    },
    dateRangeHint() {
      if (!this.filters.startDate && !this.filters.endDate)
        return "Pick a start date";
      if (this.filters.startDate && !this.filters.endDate)
        return "Pick an end date";
      const nights =
        moment(this.filters.endDate).diff(
          moment(this.filters.startDate),
          "days"
        ) + 1;
      return `${nights} day${nights === 1 ? "" : "s"} selected`;
    },
    statusCounts() {
      const counts = {};
      this.busChanges.forEach((item) => {
        if (!this.passesFilters(item, true)) return;
        const status = item.status || "UNKNOWN";
        counts[status] = (counts[status] || 0) + 1;
      });
      return Object.keys(counts)
        .map((status) => ({
          status: status,
          count: counts[status],
          hex: this.getStatusHex(status),
        }))
        .sort(
          (a, b) =>
            this.getStatusWeight(a.status) - this.getStatusWeight(b.status)
        );
    },
    uniqueBusCount() {
      const set = {};
      this.filteredBusChanges.forEach((item) => {
        [item.pickupBusNumber, item.dropoffBusNumber, item.primaryBusNumber]
          .filter(Boolean)
          .forEach((bus) => {
            set[String(bus).trim()] = true;
          });
      });
      return Object.keys(set).length;
    },
    activeFilterCount() {
      const f = this.filters;
      let count = 0;
      if (f.generalSearch) count++;
      if (f.studentName) count++;
      if (f.busNumber) count++;
      if (f.phoneNumber) count++;
      if (f.branch) count++;
      if (f.status) count++;
      if (f.startDate || f.endDate) count++;
      if (f.includeCompleted) count++;
      if (f.includeDeleted) count++;
      return count;
    },
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    availableBranches() {
      const set = {};
      this.busChanges.forEach((item) => {
        if (item.branch) set[item.branch] = true;
      });
      return Object.keys(set).sort();
    },
    availableStatuses() {
      const set = {};
      this.busChanges.forEach((item) => {
        if (item.status) set[item.status] = true;
      });
      return Object.keys(set).sort();
    },
    availableBusNumbers() {
      const set = {};
      this.busRoutes.forEach((r) => {
        const num = this.getRouteBusNumber(r);
        if (num) set[String(num)] = true;
      });
      return Object.keys(set).sort((a, b) => parseInt(a) - parseInt(b));
    },
    summerSeasonName() {
      const year =
        this.summerDates && this.summerDates.startDate
          ? moment(this.summerDates.startDate).year()
          : moment().year();
      return `Summer ${year}`;
    },
    filteredBusChanges() {
      let filtered = this.busChanges.filter((item) =>
        this.passesFilters(item, false)
      );

      filtered.sort((a, b) => {
        const wa = this.getStatusWeight(a.status);
        const wb = this.getStatusWeight(b.status);
        if (wa !== wb) return wa - wb;
        if (a.startDateTimestamp !== b.startDateTimestamp) {
          return b.startDateTimestamp - a.startDateTimestamp;
        }
        return this.normalizeText(a.studentName).localeCompare(
          this.normalizeText(b.studentName)
        );
      });

      return filtered;
    },
  },
  watch: {
    "filters.status"(val) {
      if (!val) return;
      const upper = val.toUpperCase();
      if (upper === "COMPLETED") this.filters.includeCompleted = true;
      if (upper === "DELETED") this.filters.includeDeleted = true;
    },
    dateRangeMenu(open) {
      if (open) {
        this.syncCalendarToRange();
      } else {
        this.rangeHoverDate = "";
      }
    },
  },
  created() {
    this.loadDispatcherData();
  },
  methods: {
    // ignoreStatus lets the results bar count each status within the other filters.
    passesFilters(item, ignoreStatus) {
      const normalizedGeneral = this.normalizeText(this.filters.generalSearch);
      const normalizedBus = this.normalizeText(this.filters.busNumber);
      const normalizedStudent = this.normalizeText(this.filters.studentName);
      const normalizedPhone = this.normalizePhone(this.filters.phoneNumber);
      const filterStart = this.filters.startDate || "";
      const filterEnd = this.filters.endDate || "";

      const status = item.status ? item.status.toUpperCase() : "";
      if (!this.filters.includeCompleted && status === "COMPLETED") return false;
      if (!this.filters.includeDeleted && status === "DELETED") return false;

      if (filterStart || filterEnd) {
        const itemStart = item.startDateForFilter || "";
        const itemEnd = item.endDateForFilter || "9999-12-31";
        const rangeStart = filterStart || "0000-01-01";
        const rangeEnd = filterEnd || "9999-12-31";
        if (!(itemStart <= rangeEnd && itemEnd >= rangeStart)) return false;
      }

      if (this.filters.branch && item.branch !== this.filters.branch)
        return false;
      if (
        !ignoreStatus &&
        this.filters.status &&
        item.status !== this.filters.status
      )
        return false;

      if (
        normalizedBus &&
        !this.normalizeText(item.primaryBusNumber).includes(normalizedBus) &&
        !this.normalizeText(item.pickupBusNumber).includes(normalizedBus) &&
        !this.normalizeText(item.dropoffBusNumber).includes(normalizedBus) &&
        !this.normalizeText(item.pickupRouteLabel).includes(normalizedBus) &&
        !this.normalizeText(item.dropoffRouteLabel).includes(normalizedBus)
      ) {
        return false;
      }

      if (
        normalizedPhone &&
        !this.normalizePhone(item.busChangePhoneNumber).includes(
          normalizedPhone
        ) &&
        !this.normalizePhone(item.notificationPhoneNumber).includes(
          normalizedPhone
        ) &&
        !this.normalizePhone(item.geoPhoneNumber).includes(normalizedPhone) &&
        !this.normalizePhone(item.homePhone).includes(normalizedPhone)
      ) {
        return false;
      }

      if (
        normalizedStudent &&
        !this.normalizeText(item.studentName).includes(normalizedStudent) &&
        !this.normalizeText(item.studentNameHeb).includes(normalizedStudent)
      ) {
        return false;
      }

      if (normalizedGeneral) {
        const generalPhone = this.normalizePhone(this.filters.generalSearch);
        const textMatch = this.normalizeText(
          [
            item.studentName,
            item.studentNameHeb,
            item.familyID,
            item.studentID,
            item.branch,
            item.class,
            item.busChangeName,
            item.busChangeAddress,
            item.homeAddress,
            item.note,
            item.primaryBusNumber,
            item.pickupBusNumber,
            item.dropoffBusNumber,
            item.pickupRouteLabel,
            item.dropoffRouteLabel,
            item.teacherH,
            item.teacherH2,
            item.dcRoomNum,
            item.roomNum,
          ].join(" ")
        ).includes(normalizedGeneral);
        const phoneMatch =
          generalPhone.length >= 4 &&
          (this.normalizePhone(item.busChangePhoneNumber).includes(generalPhone) ||
            this.normalizePhone(item.notificationPhoneNumber).includes(generalPhone) ||
            this.normalizePhone(item.geoPhoneNumber).includes(generalPhone) ||
            this.normalizePhone(item.homePhone).includes(generalPhone));
        if (!textMatch && !phoneMatch) return false;
      }

      return true;
    },
    toggleStatusFilter(status) {
      this.filters.status = this.filters.status === status ? null : status;
    },
    async loadDispatcherData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadUserDayCampBuildings(),
          this.loadSummerDates(),
        ]);
        await this.loadBusRoutes();
        await this.loadBusChanges();
        await this.loadTeacherInfo();
        await this.loadRouteStops();
      } finally {
        this.loading = false;
      }
    },
    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];
      if (!isAdmin || isAdmin == "false") return false;
      return true;
    },
    async loadUserDayCampBuildings() {
      const username = this.$store.state.authentication.user;

      if (!username) {
        this.userDayCampBuildings = [];
        return;
      }

      try {
        const attributes = await this.$http.get(
          "api/users/GetUserAttributesByUsername?username=" +
            encodeURIComponent(username)
        );

        this.userDayCampBuildings = (attributes || [])
          .filter((a) => a && a.name && a.name.trim() === "DayCampBuilding")
          .map((a) => (a.value ? a.value.trim() : ""))
          .filter(Boolean);
      } catch (e) {
        this.userDayCampBuildings = [];
      }
    },
    hasUserDayCampBuildings() {
      return !this.isAdmin() && this.userDayCampBuildings.length > 0;
    },
    useDayCampBuildingFilter() {
      return this.hasUserDayCampBuildings() && this.isWithinSummerSeason();
    },
    async loadSummerDates() {
      if (this.summerDates) return;
      try {
        const response = await this.$http.get("api/daycamp/getSummerDates");
        if (response && response.startDate && response.endDate) {
          this.summerDates = {
            startDate: response.startDate,
            endDate: response.endDate,
          };
        }
      } catch (e) {
        this.summerDates = null;
      }
    },
    isWithinSummerSeason() {
      return this.isDateInSummerSeason(this.todayDate);
    },
    isDateInSummerSeason(date) {
      if (!this.summerDates || !date) return false;
      const day = moment(date).startOf("day");
      const start = moment(this.summerDates.startDate).startOf("day");
      const end = moment(this.summerDates.endDate).startOf("day");
      if (!day.isValid() || !start.isValid() || !end.isValid()) return false;
      return day.isSameOrAfter(start) && day.isSameOrBefore(end);
    },
    isBusChangeInSummerSeason(busChange) {
      if (!this.summerDates) return false;
      const seasonStart = moment(this.summerDates.startDate).startOf("day");
      const seasonEnd = moment(this.summerDates.endDate).startOf("day");
      if (!seasonStart.isValid() || !seasonEnd.isValid()) return false;
      const bcStart = busChange && busChange.startDate
        ? moment(busChange.startDate).startOf("day")
        : null;
      const bcEnd = busChange && busChange.endDate
        ? moment(busChange.endDate).startOf("day")
        : null;
      if (!bcStart || !bcStart.isValid()) return this.isWithinSummerSeason();
      const end = bcEnd && bcEnd.isValid() ? bcEnd : bcStart;
      return (
        bcStart.isSameOrBefore(seasonEnd) && end.isSameOrAfter(seasonStart)
      );
    },
    getRoomDisplay(item) {
      if (!item) return "";
      if (this.isBusChangeInSummerSeason(item)) {
        return item.dcRoomNum || "";
      }
      return item.roomNum || "";
    },
    getRouteBusNumber(route) {
      // Prefer actualBusNumber from GetBusRoutes when present; else busNumber.
      // Day camp routes often only populate actualBusNumber.
      if (!route) return "";
      const actual = (route.actualBusNumber || "").toString().trim();
      if (actual) return actual;
      return (route.busNumber || "").toString().trim();
    },
    async loadBusRoutes() {
      try {
        // Same summer rule as Bus Changes: DC dates load Summer/DayCamp
        // only. Year-round stays out of the pickup/dropoff lookup.
        const dates = [
          this.todayDate,
          this.filters.startDate,
          this.filters.endDate,
        ].filter((d, index, list) => d && list.indexOf(d) === index);
        const anySummer = dates.length
          ? dates.some((d) => this.isDateInSummerSeason(d))
          : this.isWithinSummerSeason();
        const anySchool = dates.length
          ? dates.some((d) => !this.isDateInSummerSeason(d))
          : !anySummer;

        const urls = [];
        if (anySchool) {
          urls.push("api/buschange/GetBusRoutes");
        }
        if (anySummer) {
          urls.push(
            "api/buschange/GetBusRoutes?season=" +
              encodeURIComponent(this.summerSeasonName) +
              "&kind=DayCamp"
          );
        }
        if (!urls.length) {
          urls.push("api/buschange/GetBusRoutes");
        }

        const responses = await Promise.all(
          urls.map((url) => this.$http.get(url).catch(() => []))
        );
        const lookup = {};
        const merged = [];
        responses.forEach((routes) => {
          (Array.isArray(routes) ? routes : []).forEach((r) => {
            if (!r || r.id == null) return;
            const key = String(r.id);
            if (!lookup[key]) {
              lookup[key] = r;
              merged.push(r);
            }
          });
        });
        this.busRoutes = merged;
        this.busRouteLookup = lookup;
      } catch (error) {
        this.busRoutes = [];
        this.busRouteLookup = {};
        this.$notify({ type: "error", text: "Failed to load bus routes" });
      }
    },
    async loadRouteStops() {
      try {
        const routeIds = new Set();
        this.busChanges.forEach((bc) => {
          if (bc.pickupRouteID != null && bc.pickupStopNumber != null) {
            routeIds.add(bc.pickupRouteID);
          }
          if (bc.dropoffRouteID != null && bc.dropoffStopNumber != null) {
            routeIds.add(bc.dropoffRouteID);
          }
        });
        const ids = Array.from(routeIds);
        if (!ids.length) return;

        const results = await Promise.all(
          ids.map((routeId) =>
            this.$http
              .get("api/buschange/GetRouteStops?routeId=" + routeId)
              .then((stops) => ({
                routeId,
                stops: Array.isArray(stops) ? stops : [],
              }))
              .catch(() => ({ routeId, stops: [] }))
          )
        );

        const lookup = {};
        results.forEach(({ routeId, stops }) => {
          const map = {};
          stops.forEach((s) => {
            if (s && s.stopNumber != null) {
              map[String(s.stopNumber)] = s.stopAddress || "";
            }
          });
          lookup[String(routeId)] = map;
        });
        this.routeStopsLookup = lookup;

        this.busChanges = this.busChanges.map((bc) => {
          const pickupMap =
            bc.pickupRouteID != null ? lookup[String(bc.pickupRouteID)] : null;
          const dropoffMap =
            bc.dropoffRouteID != null
              ? lookup[String(bc.dropoffRouteID)]
              : null;
          const pickupStopAddress =
            pickupMap && bc.pickupStopNumber != null
              ? pickupMap[String(bc.pickupStopNumber)] || ""
              : "";
          const dropoffStopAddress =
            dropoffMap && bc.dropoffStopNumber != null
              ? dropoffMap[String(bc.dropoffStopNumber)] || ""
              : "";
          if (!pickupStopAddress && !dropoffStopAddress) return bc;
          return Object.assign({}, bc, {
            pickupStopAddress,
            dropoffStopAddress,
          });
        });
      } catch (error) {
        console.error("loadRouteStops failed:", error);
      }
    },
    async loadBusChanges() {
      try {
        // Backend: building takes precedence; branch is only used when no building filter is sent.
        // Always include every query param key the OData Get declares, even when empty.
        const useBuilding = this.useDayCampBuildingFilter();
        const branchValue =
          !useBuilding && this.userBranch ? this.userBranch : "";
        const buildingValue = useBuilding
          ? this.userDayCampBuildings.join(",")
          : "";
        const url =
          "odata/busChange?branch=" +
          branchValue +
          "&building=" +
          buildingValue +
          "&includeCompleted=true&includeDeleted=true";
        const response = await this.$http.get(url);
        const rows = response && response.value ? response.value : [];
        this.busChanges = rows.map((raw) => this.mapBusChange(raw));
      } catch (error) {
        this.busChanges = [];
        this.$notify({ type: "error", text: "Failed to load bus changes" });
      }
    },
    async loadTeacherInfo() {
      const seen = {};
      const uniqueIDs = [];
      this.busChanges.forEach((bc) => {
        const id = bc.studentID ? String(bc.studentID).trim() : "";
        if (id && !seen[id]) {
          seen[id] = true;
          uniqueIDs.push(id);
        }
      });

      if (!uniqueIDs.length) return;

      try {
        const students = await this.$http.post(
          "api/BusChange/GetStudentInfoForStudentIDs",
          uniqueIDs
        );

        if (!Array.isArray(students)) return;

        const lookup = {};
        students.forEach((s) => {
          const id = s.studentID ? String(s.studentID).trim() : "";
          if (id) lookup[id] = s;
        });

        this.busChanges = this.busChanges.map((bc) => {
          const id = bc.studentID ? String(bc.studentID).trim() : "";
          const info = id ? lookup[id] : null;
          if (!info) return bc;
          return Object.assign({}, bc, {
            teacherH: String(
              info.teacherH || info.TeacherH || ""
            ).trim(),
            teacherH2: String(
              info.teacherH2 || info.TeacherH2 || ""
            ).trim(),
            roomNum: String(info.roomNum || info.RoomNum || "").trim(),
            dcRoomNum: String(
              info.dcRoomNum || info.DcRoomNum || ""
            ).trim(),
          });
        });
      } catch (error) {
        console.error("loadTeacherInfo failed:", error);
      }
    },
    mapBusChange(raw) {
      const pickupRoute = raw.pickupRouteID
        ? this.busRouteLookup[String(raw.pickupRouteID)]
        : null;
      const dropoffRoute = raw.dropoffRouteID
        ? this.busRouteLookup[String(raw.dropoffRouteID)]
        : null;

      return {
        id: raw.id,
        studentID: raw.studentID || "",
        familyID: raw.familyID || "",
        studentName: raw.studentName || "",
        studentNameHeb: raw.studentNameHeb || "",
        branch: raw.branch || "",
        class: raw.class || "",
        building: raw.building || "",
        section: raw.section || "",
        status: raw.status || "",
        startDate: raw.startDate || "",
        endDate: raw.endDate || "",
        startPeriod: raw.startPeriod || "",
        endPeriod: raw.endPeriod || "",
        startDateForFilter: this.formatDateForFilter(raw.startDate),
        endDateForFilter: this.formatDateForFilter(raw.endDate),
        startDateTimestamp: raw.startDate
          ? new Date(raw.startDate).getTime()
          : 0,
        busChangeName: raw.busChangeName || "",
        busChangeAddress: raw.busChangeAddress || "",
        busChangePhoneNumber: raw.busChangePhoneNumber || "",
        notificationPhoneNumber: raw.notificationPhoneNumber || "",
        notificationType: raw.notificationType || "",
        geoPhoneNumber: raw.geoPhoneNumber || "",
        homePhone: raw.homePhone || "",
        homeAddress: raw.homeAddress || "",
        note: raw.note || "",
        teacherH: "",
        teacherH2: "",
        roomNum: "",
        dcRoomNum: "",
        pickupRouteID: raw.pickupRouteID || null,
        dropoffRouteID: raw.dropoffRouteID || null,
        pickupRouteLabel: this.formatRouteLabel(pickupRoute),
        dropoffRouteLabel: this.formatRouteLabel(dropoffRoute),
        pickupBusNumber: this.getRouteBusNumber(pickupRoute),
        dropoffBusNumber: this.getRouteBusNumber(dropoffRoute),
        primaryBusNumber: this.getPrimaryBusNumber(pickupRoute, dropoffRoute),
        hasDifferentBusNumbers: this.hasDifferentBusNumbers(
          pickupRoute,
          dropoffRoute
        ),
        pickupRouteColor: pickupRoute ? pickupRoute.color || "" : "",
        dropoffRouteColor: dropoffRoute ? dropoffRoute.color || "" : "",
        pickupRouteDescription: pickupRoute ? pickupRoute.description || "" : "",
        dropoffRouteDescription: dropoffRoute
          ? dropoffRoute.description || ""
          : "",
        pickupDriver: pickupRoute ? pickupRoute.driver || "" : "",
        dropoffDriver: dropoffRoute ? dropoffRoute.driver || "" : "",
        pickupStopNumber:
          raw.pickupStopNumber != null ? raw.pickupStopNumber : null,
        dropoffStopNumber:
          raw.dropoffStopNumber != null ? raw.dropoffStopNumber : null,
        pickupStopAddress: "",
        dropoffStopAddress: "",
      };
    },
    formatRouteLabel(route) {
      if (!route) return "";
      const bus = this.getRouteBusNumber(route) || "No Bus";
      const driver = route.driver ? route.driver : "No Driver";
      return `${bus} — ${driver}`;
    },
    getPrimaryBusNumber(pickupRoute, dropoffRoute) {
      const pickup = this.getRouteBusNumber(pickupRoute);
      if (pickup) return pickup;
      const dropoff = this.getRouteBusNumber(dropoffRoute);
      if (dropoff) return dropoff;
      return "";
    },
    hasDifferentBusNumbers(pickupRoute, dropoffRoute) {
      const pickup = this.getRouteBusNumber(pickupRoute);
      const dropoff = this.getRouteBusNumber(dropoffRoute);
      return !!(pickup && dropoff && pickup !== dropoff);
    },
    getRouteColor(colorName) {
      if (!colorName) return null;

      const colorUpper = colorName.toUpperCase().trim();
      const colorMap = {
        BEIGE: "#F5F5DC",
        BLACK: "#000000",
        BLUE: "#0000FF",
        BROWN: "#8B4513",
        GOLD: "#FFD700",
        GREEN: "#008000",
        ORANGE: "#FFA500",
        PINK: "#FFC0CB",
        PURPLE: "#800080",
        RED: "#FF0000",
        SILVER: "#C0C0C0",
        WHITE: "#E0E0E0",
        YELLOW: "#FFFF00",
        "DARK BLUE": "#00008B",
        "DARK GREEN": "#006400",
        "DARK PINK": "#FF1493",
        "DARK PURPLE": "#4B0082",
        "LIGHT BLUE": "#ADD8E6",
        "LIGHT GREEN": "#90EE90",
        "LIGHT PINK": "#FFB6C1",
        "LIGHT PURPLE": "#DDA0DD",
        GRAY: "#808080",
        GREY: "#808080",
        CYAN: "#00FFFF",
        TEAL: "#008080",
        LIME: "#00FF00",
        INDIGO: "#4B0082",
        NAVY: "#000080",
        MAROON: "#800000",
        OLIVE: "#808000",
        VIOLET: "#EE82EE",
        TURQUOISE: "#40E0D0",
        CORAL: "#FF7F50",
        SALMON: "#FA8072",
        MINT: "#98FF98",
        LAVENDER: "#E6E6FA",
        PEACH: "#FFE5B4",
        SKY: "#87CEEB",
        ROSE: "#FF007F",
      };

      return colorMap[colorUpper] || "#5cb860";
    },
    getRouteChipStyle(route) {
      if (!route || !route.color) {
        return {
          background: "#455a64",
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      const bgColor = this.getRouteColor(route.color);
      if (!bgColor) {
        return {
          background: "#455a64",
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      const colorUpper = route.color.toUpperCase().trim();
      const isLightBackground = [
        "BEIGE",
        "WHITE",
        "YELLOW",
        "LIGHT BLUE",
        "LIGHT GREEN",
        "LIGHT PINK",
        "LIGHT PURPLE",
        "SILVER",
        "GOLD",
        "PINK",
        "ORANGE",
        "LAVENDER",
        "PEACH",
        "MINT",
      ].includes(colorUpper);

      if (
        colorUpper === "WHITE" ||
        bgColor.toUpperCase() === "#FFF" ||
        bgColor.toUpperCase() === "#FFFFFF"
      ) {
        return {
          background: "#f5f5f5",
          color: "#000000",
          border: "2px solid #cccccc",
        };
      }

      return {
        background: bgColor,
        color: isLightBackground ? "#000000" : "#FFFFFF",
        border: isLightBackground
          ? "1.5px solid rgba(0, 0, 0, 0.3)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      };
    },
    normalizeText(value) {
      if (value === null || value === undefined) return "";
      return value
        .toString()
        .trim()
        .toLowerCase();
    },
    normalizePhone(value) {
      if (!value) return "";
      return value
        .toString()
        .replace(/\D/g, "")
        .trim();
    },
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return "—";
      const cleaned = this.normalizePhone(phoneNumber);
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
          6
        )}`;
      }
      return phoneNumber;
    },
    // Easy-to-read single-line schedule format: "Wednesday 9-16-26 PM"
    formatShortSchedule(dateString, period) {
      const datePart = dateString
        ? moment(dateString).format("dddd M-D-YY")
        : "Open";
      const periodPart = this.formatPeriodShort(period);
      return periodPart ? `${datePart} ${periodPart}` : datePart;
    },
    isSameFilterDate(a, b) {
      const left = this.formatDateForFilter(a);
      const right = this.formatDateForFilter(b);
      return left && right && left === right;
    },
    // Only show "→ end" when the end date/period actually differs from the start
    // (e.g. a single afternoon-only change shows just one date/period).
    showScheduleEnd(busChange) {
      if (!busChange) return false;
      const hasEndDate = !!busChange.endDate;
      const hasEndPeriod = !!busChange.endPeriod;
      if (!hasEndDate && !hasEndPeriod) return false;
      const sameDate = this.isSameFilterDate(
        busChange.startDate,
        busChange.endDate
      );
      const samePeriod =
        this.normalizeText(busChange.startPeriod) ===
        this.normalizeText(busChange.endPeriod);
      return !(sameDate && samePeriod);
    },
    toggleActiveToday() {
      if (this.isActiveToday) {
        this.clearDateRange();
      } else {
        this.filters.startDate = this.todayDate;
        this.filters.endDate = this.todayDate;
      }
    },
    toggleActiveYesterday() {
      if (this.isActiveYesterday) {
        this.clearDateRange();
      } else {
        this.filters.startDate = this.yesterdayDate;
        this.filters.endDate = this.yesterdayDate;
      }
    },
    // First click starts a new range; second click closes it.
    selectCalendarDate(date) {
      const { startDate, endDate } = this.filters;
      if (!startDate || endDate || date < startDate) {
        this.filters.startDate = date;
        this.filters.endDate = "";
      } else {
        this.filters.endDate = date;
      }
    },
    cellClasses(cell) {
      if (!cell.date) return "dr-cell--blank";
      const start = this.filters.startDate;
      const end = this.filters.endDate || this.pendingRangeEnd;
      const isStart = !!start && cell.date === start;
      const isEnd = !!end && cell.date === end;
      return {
        "dr-cell--start": isStart,
        "dr-cell--end": isEnd,
        "dr-cell--single": isStart && !end,
        "dr-cell--between":
          !!start && !!end && cell.date > start && cell.date < end,
        "dr-cell--preview": !this.filters.endDate && !!this.pendingRangeEnd,
        "dr-cell--today": cell.date === this.todayDate,
      };
    },
    shiftCalendar(direction) {
      this.calendarMonth = moment(this.calendarMonth)
        .add(direction, "month")
        .format("YYYY-MM-DD");
    },
    syncCalendarToRange() {
      const anchor = this.filters.startDate || this.todayDate;
      this.calendarMonth = moment(anchor)
        .startOf("month")
        .format("YYYY-MM-DD");
    },
    applyDatePreset(preset) {
      if (preset === "today") {
        this.filters.startDate = this.todayDate;
        this.filters.endDate = this.todayDate;
      } else if (preset === "yesterday") {
        this.filters.startDate = this.yesterdayDate;
        this.filters.endDate = this.yesterdayDate;
      } else if (preset === "thisWeek") {
        this.filters.startDate = moment()
          .startOf("week")
          .format("YYYY-MM-DD");
        this.filters.endDate = moment()
          .endOf("week")
          .format("YYYY-MM-DD");
      } else if (preset === "next7") {
        this.filters.startDate = this.todayDate;
        this.filters.endDate = moment()
          .add(7, "days")
          .format("YYYY-MM-DD");
      } else {
        this.clearDateRange();
      }
      this.dateRangeMenu = false;
    },
    formatShortDate(dateString) {
      if (!dateString) return "";
      return moment(dateString).format("M/D/YYYY");
    },
    clearDateRange() {
      this.filters.startDate = "";
      this.filters.endDate = "";
    },
    formatDateForFilter(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatPeriod(period) {
      if (!period) return "—";
      const n = period
        .toString()
        .trim()
        .toUpperCase();
      if (n === "AM") return "Morning Pickup";
      if (n === "PM") return "Afternoon Dropoff";
      return period;
    },
    formatPeriodShort(period) {
      if (!period) return "";
      const n = period
        .toString()
        .trim()
        .toUpperCase();
      if (n === "AM") return "AM";
      if (n === "PM") return "PM";
      return period;
    },
    getStatusIcon(status) {
      const n = status
        ? status
            .toString()
            .trim()
            .toUpperCase()
        : "";
      if (n === "NEW") return "mdi-new-box";
      if (n === "FUTURE" || n === "PENDING") return "mdi-clock-outline";
      if (n === "ACTIVE") return "mdi-check-circle";
      if (n === "COMPLETED") return "mdi-check-all";
      if (n === "DELETED") return "mdi-delete";
      return "mdi-information";
    },
    getStatusColor(status) {
      const n = status
        ? status
            .toString()
            .trim()
            .toUpperCase()
        : "";
      if (n === "NEW") return "blue";
      if (n === "FUTURE" || n === "PENDING") return "orange";
      if (n === "ACTIVE") return "green";
      if (n === "COMPLETED") return "grey";
      if (n === "DELETED") return "red";
      return "indigo";
    },
    getStatusHex(status) {
      const n = status
        ? status
            .toString()
            .trim()
            .toUpperCase()
        : "";
      if (n === "NEW") return "#2196f3";
      if (n === "FUTURE" || n === "PENDING") return "#ff9800";
      if (n === "ACTIVE") return "#4caf50";
      if (n === "COMPLETED") return "#9e9e9e";
      if (n === "DELETED") return "#f44336";
      return "#5c6bc0";
    },
    getStatusWeight(status) {
      const n = status
        ? status
            .toString()
            .trim()
            .toUpperCase()
        : "";
      if (n === "ACTIVE") return 1;
      if (n === "PENDING" || n === "FUTURE") return 2;
      if (n === "NEW") return 3;
      if (n === "COMPLETED") return 4;
      if (n === "DELETED") return 5;
      return 6;
    },
    clearFilters() {
      this.filters.generalSearch = "";
      this.filters.busNumber = "";
      this.filters.phoneNumber = "";
      this.filters.studentName = "";
      this.filters.branch = null;
      this.filters.status = null;
      this.filters.includeCompleted = false;
      this.filters.includeDeleted = false;
      this.clearDateRange();
    },
  },
};
</script>

<style>
/* ── hero ── */
.bus-dispatching-page .dispatch-hero {
  background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 45%, #4caf50 100%);
  border-radius: 8px;
  padding: 6px 16px;
  color: #fff;
}

.bus-dispatching-page .dispatch-hero-icon {
  color: #fff !important;
  opacity: 0.9;
}

.bus-dispatching-page .dispatch-hero-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
}

.bus-dispatching-page .dispatch-hero-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.82);
  margin-left: 12px;
}

.bus-dispatching-page .dispatch-scope-chip {
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  padding: 2px 9px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  flex-shrink: 0;
}

.bus-dispatching-page .dispatch-refresh-btn {
  border-color: rgba(255, 255, 255, 0.7) !important;
  color: #fff !important;
  font-weight: 600;
}

/* ── filter card ── */
.bus-dispatching-page .filter-card {
  border-radius: 10px;
  border: 1px solid #c8e6c9;
  overflow: hidden;
}

.bus-dispatching-page .filter-header {
  display: flex;
  align-items: center;
  background: #f1f8f1;
  border-bottom: 1px solid #c8e6c9;
  padding: 1px 16px;
}

.bus-dispatching-page .filter-header-icon {
  color: #2e7d32 !important;
  font-size: 18px !important;
}

.bus-dispatching-page .filter-header-title {
  font-size: 13px;
  font-weight: 700;
  color: #2e7d32;
  letter-spacing: 0.3px;
}

.bus-dispatching-page .filter-active-chip {
  font-size: 11px !important;
  font-weight: 700;
  height: 20px !important;
}

.bus-dispatching-page .filter-clear-btn {
  color: #78909c !important;
  font-size: 12px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.bus-dispatching-page .filter-body {
  padding: 10px 16px;
}

/* ── date range menu ── */
.date-range-card {
  padding: 10px 12px 6px;
}

.date-range-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eceff1;
}

.date-range-preset {
  padding: 4px 12px;
  border-radius: 14px;
  border: 1px solid #cfd8dc;
  background: #fafafa;
  color: #546e7a;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
}

.date-range-preset:hover {
  background: #e8f5e9;
  border-color: #4caf50;
  color: #1b5e20;
}

/* ── two-month range calendar ── */
.dr-cal {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 12px 4px 4px;
}

.dr-cal-month {
  width: 266px;
}

.dr-cal-head {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.dr-cal-title {
  flex: 1;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}

.dr-cal-nav {
  margin: 0 !important;
}

.dr-cal-nav--hidden {
  visibility: hidden;
}

.dr-cal-dow {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.dr-cal-dow span {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #90a4ae;
  padding-bottom: 4px;
}

.dr-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 2px;
}

.dr-cell {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.dr-cell--blank {
  cursor: default;
}

.dr-day {
  width: 34px;
  height: 34px;
  line-height: 34px;
  text-align: center;
  border-radius: 50%;
  font-size: 14px;
  color: #37474f;
}

.dr-cell:not(.dr-cell--blank):hover .dr-day {
  background: #e8f5e9;
}

.dr-cell--today .dr-day {
  font-weight: 700;
  box-shadow: inset 0 0 0 1px #b0bec5;
}

/* continuous band between the two endpoints */
.dr-cell--between {
  background: #e8f5e9;
}

.dr-cell--start:not(.dr-cell--single) {
  background: linear-gradient(to right, transparent 50%, #e8f5e9 50%);
}

.dr-cell--end:not(.dr-cell--start) {
  background: linear-gradient(to right, #e8f5e9 50%, transparent 50%);
}

.dr-cell--preview.dr-cell--between {
  background: #f1f8e9;
}

/* single day picked — no band on either side */
.dr-cell--start.dr-cell--end,
.dr-cell--single {
  background: transparent;
}

.dr-cell--start .dr-day,
.dr-cell--end .dr-day {
  background: #2e7d32 !important;
  color: #fff;
  font-weight: 700;
  box-shadow: none;
}

.date-range-actions {
  display: flex;
  align-items: center;
  border-top: 1px solid #eceff1;
  margin-top: 6px;
  padding-top: 4px;
}

.date-range-hint {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  padding-left: 6px;
}

.bus-dispatching-page .filter-search-wrap .v-input__slot {
  box-shadow: 0 2px 8px rgba(46, 125, 50, 0.12) !important;
  border: 1.5px solid #c8e6c9 !important;
}

.bus-dispatching-page .filter-group-label {
  font-size: 11px;
  font-weight: 700;
  color: #78909c;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}

.bus-dispatching-page .filter-fields-row {
  margin: 0 -4px;
}

.bus-dispatching-page .filter-field-wrap {
  padding: 0 4px 6px;
}

.bus-dispatching-page .filter-toggle-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f4f0;
}

.bus-dispatching-page .filter-toggle-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  border: 1.5px solid #cfd8dc;
  color: #78909c;
  background: #fafafa;
  transition: all 0.18s ease;
}

.bus-dispatching-page .filter-toggle-pill:hover {
  border-color: #90a4ae;
  color: #546e7a;
}

.bus-dispatching-page .filter-toggle-pill--on {
  background: #e8f5e9;
  border-color: #4caf50;
  color: #1b5e20;
}

.bus-dispatching-page .filter-toggle-pill--on .v-icon {
  color: #1b5e20 !important;
}

.bus-dispatching-page .filter-toggle-pill--yesterday {
  background: #fff8e1;
  border-color: #f9a825;
  color: #e65100;
}

.bus-dispatching-page .filter-toggle-pill--yesterday .v-icon {
  color: #e65100 !important;
}

.bus-dispatching-page .filter-toggle-pill--completed {
  background: #e0f2f1;
  border-color: #00897b;
  color: #004d40;
}

.bus-dispatching-page .filter-toggle-pill--completed .v-icon {
  color: #004d40 !important;
}

.bus-dispatching-page .filter-toggle-pill--deleted {
  background: #fce4ec;
  border-color: #e53935;
  color: #b71c1c;
}

.bus-dispatching-page .filter-toggle-pill--deleted .v-icon {
  color: #b71c1c !important;
}

/* ── results bar ── */
.bus-dispatching-page .results-bar {
  margin-top: 2px;
}

.bus-dispatching-page .results-bar-inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  background: #fff;
  border: 1px solid #e0e6ef;
  border-radius: 8px;
  padding: 6px 12px;
}

.bus-dispatching-page .results-count-block {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.bus-dispatching-page .results-count-num {
  font-size: 20px;
  font-weight: 800;
  color: #1b5e20;
  line-height: 1;
}

.bus-dispatching-page .results-count-label {
  font-size: 12px;
  color: #78909c;
  font-weight: 600;
}

.bus-dispatching-page .results-divider {
  width: 1px;
  height: 20px;
  background: #e0e6ef;
}

.bus-dispatching-page .results-status-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.bus-dispatching-page .results-status-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  border: 1px solid;
  background: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
}

.bus-dispatching-page .results-status-chip:hover {
  filter: brightness(0.95);
}

.bus-dispatching-page .results-status-chip--on {
  color: #fff !important;
}

.bus-dispatching-page .results-empty-note {
  font-size: 12px;
  color: #b0bec5;
  font-weight: 600;
}

.bus-dispatching-page .results-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}

.bus-dispatching-page .results-meta-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #546e7a;
}

.bus-dispatching-page .results-meta-item .v-icon {
  color: #90a4ae !important;
  font-size: 15px !important;
}

/* ── panels container ── */
.bus-dispatching-page .dispatch-panels {
  box-shadow: none !important;
}

.bus-dispatching-page .dispatch-panels .v-expansion-panel__container {
  border: 1px solid #e0e6ef !important;
  border-radius: 10px !important;
  margin-bottom: 8px !important;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07) !important;
}

.bus-dispatching-page
  .dispatch-panels
  .v-expansion-panel__container
  + .v-expansion-panel__container {
  border-top: 1px solid #e0e6ef !important;
}

/* ── panel header ── */
.bus-dispatching-page .v-expansion-panel__header {
  padding: 0 !important;
  min-height: 72px;
}

.bus-dispatching-page .v-expansion-panel__header__icon {
  padding-right: 10px;
}

.bus-dispatching-page .panel-header-inner {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 72px;
  padding-right: 12px;
}

.bus-dispatching-page .panel-status-bar {
  width: 5px;
  min-height: 72px;
  border-radius: 0;
  flex-shrink: 0;
  margin-right: 16px;
}

.bus-dispatching-page .panel-col {
  flex-shrink: 0;
  padding-right: 20px;
}

.bus-dispatching-page .panel-name-col {
  flex: 1 1 0;
  min-width: 0;
}

.bus-dispatching-page .panel-bus-col {
  width: 160px;
}

.bus-dispatching-page .panel-bus-dual-row {
  display: flex;
  flex-direction: row;
  gap: 6px;
  margin-top: 2px;
}

.bus-dispatching-page .panel-bus-badge--sm {
  font-size: 16px;
  padding: 3px 7px;
}

.bus-dispatching-page .panel-dates-col {
  width: 290px;
}

.bus-dispatching-page .panel-contact-col {
  width: 220px;
  overflow: hidden;
}

.bus-dispatching-page .panel-contact-name {
  font-size: 13px;
  font-weight: 600;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.bus-dispatching-page .panel-contact-address {
  font-size: 12px;
  color: #546e7a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.bus-dispatching-page .panel-contact-phone {
  font-size: 13px;
  font-weight: 700;
  color: #2e7d32;
  display: flex;
  align-items: center;
}

.bus-dispatching-page .panel-contact-teacher {
  font-size: 12px;
  color: #546e7a;
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: 2px;
}

.bus-dispatching-page .panel-teacher-text {
  direction: rtl;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.bus-dispatching-page .panel-status-col {
  width: 120px;
  display: flex;
  align-items: center;
  padding-right: 0;
}

.bus-dispatching-page .panel-name-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.bus-dispatching-page .panel-name-line .panel-student-name {
  min-width: 0;
  flex-shrink: 1;
}

.bus-dispatching-page .panel-student-name {
  font-size: 13px;
  font-weight: 500;
  color: #546e7a;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bus-dispatching-page .panel-student-heb {
  font-size: 20px;
  font-weight: 700;
  color: #1a2f45;
  line-height: 1.2;
  direction: rtl;
  white-space: nowrap;
  flex-shrink: 0;
}

.bus-dispatching-page .panel-ids {
  font-size: 12px;
  color: #78909c;
  margin-top: 2px;
}

.bus-dispatching-page .panel-home-row {
  font-size: 12px;
  color: #546e7a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.bus-dispatching-page .panel-home-label {
  font-weight: 700;
  color: #78909c;
  margin-right: 4px;
}

.bus-dispatching-page .panel-home-val {
  font-weight: 600;
  color: #37474f;
}

/* ── schedule (single line, consistent fonts on both sides) ── */
.bus-dispatching-page .panel-schedule-value {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.bus-dispatching-page .panel-schedule-value .schedule-part {
  font-size: 14px;
  font-weight: 600;
  color: #263238;
  white-space: nowrap;
}

.bus-dispatching-page .panel-schedule-value .schedule-arrow {
  font-size: 14px !important;
  color: #90a4ae !important;
}

.bus-dispatching-page .panel-field-label {
  font-size: 10px;
  font-weight: 700;
  color: #90a4ae;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.bus-dispatching-page .panel-field-value {
  font-size: 14px;
  font-weight: 600;
  color: #263238;
}

.bus-dispatching-page .panel-bus-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 6px;
  border: 2px solid #4caf50;
  font-size: 20px;
  font-weight: 800;
  color: #1a2f45;
  background: #f8fffe;
  margin-top: 2px;
}

.bus-dispatching-page .panel-bus-dir {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #78909c;
  margin-right: 5px;
  line-height: 1;
}

.bus-dispatching-page .panel-status-chip {
  font-size: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
}

/* ── panel body ── */
.bus-dispatching-page .panel-body {
  padding: 0 16px 20px 21px;
  border-top: 1px solid #f0f4f8;
  background: #fafcff;
}

.bus-dispatching-page .detail-section {
  padding: 14px 12px 10px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8edf3;
  margin-top: 12px;
}

.bus-dispatching-page .detail-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #455a64;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.bus-dispatching-page .detail-field {
  display: flex;
  align-items: flex-start;
  margin-bottom: 7px;
}

.bus-dispatching-page .detail-label {
  font-size: 13px;
  font-weight: 700;
  color: #546e7a;
  width: 130px;
  flex-shrink: 0;
}

.bus-dispatching-page .detail-val {
  font-size: 15px;
  font-weight: 500;
  color: #1a237e;
  flex: 1;
}

.bus-dispatching-page .note-val {
  font-style: italic;
  color: #546e7a;
}

.bus-dispatching-page .home-phone-val {
  font-size: 13px;
  font-weight: 600;
  color: #546e7a;
}

/* ── phone rows ── */
.bus-dispatching-page .phone-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.bus-dispatching-page .phone-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex: 1;
  min-width: 130px;
}

.bus-dispatching-page .phone-icon {
  margin-top: 14px;
  color: #78909c !important;
}

.bus-dispatching-page .phone-number {
  font-size: 15px;
  font-weight: 700;
  color: #2e7d32;
}

.bus-dispatching-page .phone-sub {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: #546e7a;
}

/* ── secondary / de-emphasized info (notification, geo phone) ── */
.bus-dispatching-page .minor-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.bus-dispatching-page .minor-info-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #90a4ae;
}

.bus-dispatching-page .minor-info-icon {
  color: #b0bec5 !important;
}

.bus-dispatching-page .minor-info-label {
  font-weight: 600;
}

.bus-dispatching-page .minor-info-val {
  color: #90a4ae;
}

/* ── lookup ids (family id / student id) ── */
.bus-dispatching-page .lookup-ids-row {
  display: flex;
  gap: 12px;
  margin-top: 4px;
  margin-bottom: 4px;
}

.bus-dispatching-page .lookup-id-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #b0bec5;
}

.bus-dispatching-page .lookup-id-label {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.bus-dispatching-page .lookup-id-val {
  font-family: "Roboto Mono", monospace;
  color: #90a4ae;
  user-select: all;
}

/* ── bus info cards (display-only, mirrors route/stop picker) ── */
.bus-dispatching-page .bus-cards-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.bus-dispatching-page .bus-card {
  background: #fafbfc;
  border: 1px solid #e8edf3;
  border-radius: 8px;
  padding: 10px 12px;
  flex: 1 1 130px;
  min-width: 0;
}

.bus-dispatching-page .bus-card-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bus-dispatching-page .bus-badge-lg {
  min-width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
  padding: 0 4px;
}

.bus-dispatching-page .bus-card-details {
  min-width: 0;
  flex: 1;
}

.bus-dispatching-page .bus-card-route-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #90a4ae;
}

.bus-dispatching-page .bus-card-desc {
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.bus-dispatching-page .bus-card-driver {
  font-size: 11px;
  color: #78909c;
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 2px;
}

.bus-dispatching-page .bus-card-stop {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #dfe6ec;
  font-size: 12px;
  color: #607d8b;
}

.bus-dispatching-page .stop-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #2e7d32;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.bus-dispatching-page .stop-address {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bus-dispatching-page .bus-card-stop--empty {
  color: #b0bec5;
  font-style: italic;
}

.bus-dispatching-page .stop-empty-icon {
  color: #cfd8dc !important;
}

/* ── teacher info box ── */
.bus-dispatching-page .teacher-info-box {
  background: #f1f8f1;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 10px 12px;
}

.bus-dispatching-page .teacher-info-title {
  font-size: 12px;
  font-weight: 700;
  color: #2e7d32;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.bus-dispatching-page .teacher-fields-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
}

.bus-dispatching-page .teacher-row {
  display: flex;
  align-items: baseline;
}

.bus-dispatching-page .teacher-label {
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
  width: 64px;
  flex-shrink: 0;
}

.bus-dispatching-page .teacher-val {
  font-size: 14px;
  font-weight: 700;
  color: #1b5e20;
}

.bus-dispatching-page .teacher-val--rtl {
  direction: rtl;
}
</style>
